import {useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {useIntl} from 'react-intl';

// Most browsers show their own generic prompt here and ignore the returnValue string, but it
// still has to be set to a truthy value for the prompt to appear at all — see
// https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event.
function handleBeforeUnload(event: BeforeUnloadEvent): void {
	event.preventDefault();
	event.returnValue = '';
}

// Warns the author before they lose unsaved Studio edits, whether they're closing/refreshing the
// tab (native `beforeunload` prompt) or navigating to another in-app route (blocked via the
// router's `history.block`, backed by our own `window.confirm` rather than the browser's
// `getUserConfirmation` — the app installs a no-op `getUserConfirmation` globally so the shared
// `useRouterPrompt` hook can drive its own modal instead, which would leave a plain string prompt
// here hanging forever). Both guards are only registered while `isDirty` is true, and torn down on
// cleanup so a saved draft (or unmount) never blocks navigation.
export function useWarnOnUnsavedChanges(isDirty: boolean): void {
	const history = useHistory();
	const intl = useIntl();

	useEffect(() => {
		if (!isDirty) {
			return undefined;
		}

		window.addEventListener('beforeunload', handleBeforeUnload);
		const unblock = history.block(() => {
			const confirmedLeave = window.confirm(intl.formatMessage({id: 'common.save_changes_text'}));
			return confirmedLeave ? undefined : false;
		});

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			unblock();
		};
	}, [isDirty, history, intl]);
}
